import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatUSD, formatDate } from "@/lib/utils";
import { Loader2, TrendingUp, RefreshCw } from "lucide-react";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "custom", label: "Custom" },
];

function KpiCard({ label, value, tone = "slate", testid }) {
  const toneCls = {
    slate: "text-slate-900",
    emerald: "text-emerald-600",
    red: "text-red-600",
    violet: "text-violet-600",
  }[tone];
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"
      data-testid={testid}
    >
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div
        className={`mt-1 text-2xl font-bold ${toneCls}`}
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {value}
      </div>
    </div>
  );
}

export default function ProviderStatsPanel() {
  const [range, setRange] = useState("today");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { range };
      if (range === "custom") {
        if (!start || !end) {
          toast.error("Pick a start and end date");
          setLoading(false);
          return;
        }
        params.start = start;
        params.end = end;
      }
      const { data } = await api.get("/admin/providers/stats", { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (range !== "custom") {
      load();
    }
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="provider-stats-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            Provider statistics
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Aggregated per-provider performance for the selected period.
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`stats-range-${r.key}`}
              className={
                "h-8 px-3 rounded-lg text-xs font-semibold border " +
                (range === r.key
                  ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {range === "custom" && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="h-9 px-2 rounded-lg border border-slate-300 text-sm"
            data-testid="stats-custom-start"
          />
          <span className="text-slate-500 text-sm">→</span>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="h-9 px-2 rounded-lg border border-slate-300 text-sm"
            data-testid="stats-custom-end"
          />
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#0F62FE] text-[#0F62FE] text-sm font-semibold hover:bg-[#EDF5FF]"
            data-testid="stats-custom-apply"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Apply
          </button>
        </div>
      )}

      {loading || !data ? (
        <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading stats…
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-500">
            Period: {formatDate(data.start)} → {formatDate(data.end)}
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard
              label="Total topups"
              value={data.totals.total_topups}
              testid="stats-kpi-total"
            />
            <KpiCard
              label="Success rate"
              value={`${data.totals.success_rate.toFixed(1)}%`}
              tone="emerald"
              testid="stats-kpi-success"
            />
            <KpiCard
              label="Failovers"
              value={data.totals.total_failovers}
              tone="violet"
              testid="stats-kpi-failovers"
            />
            <KpiCard
              label="Most used"
              value={data.totals.most_used_provider || "—"}
              testid="stats-kpi-most-used"
            />
          </div>

          {/* Per-provider table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-900">
                Per-provider breakdown
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                  <tr>
                    <th className="px-4 py-3 text-left">Provider</th>
                    <th className="px-4 py-3 text-right">Topups</th>
                    <th className="px-4 py-3 text-right">Success</th>
                    <th className="px-4 py-3 text-right">Failed</th>
                    <th className="px-4 py-3 text-right">Success rate</th>
                    <th className="px-4 py-3 text-right">Send USD</th>
                    <th className="px-4 py-3 text-right">Margin</th>
                    <th className="px-4 py-3 text-right">Avg latency</th>
                    <th className="px-4 py-3 text-right">Failover wins</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.per_provider.map((p) => (
                    <tr
                      key={p.provider_key}
                      className="hover:bg-slate-50"
                      data-testid={`stats-row-${p.provider_key}`}
                    >
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {p.provider_key}
                      </td>
                      <td className="px-4 py-3 text-right">{p.total_topups}</td>
                      <td className="px-4 py-3 text-right text-emerald-600 font-semibold">
                        {p.success_count}
                      </td>
                      <td className="px-4 py-3 text-right text-red-600">
                        {p.failure_count}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {p.success_rate.toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {formatUSD(p.total_send_usd)}
                      </td>
                      <td className="px-4 py-3 text-right text-violet-600 whitespace-nowrap">
                        {formatUSD(p.margin_usd)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {p.avg_latency_ms != null ? `${p.avg_latency_ms} ms` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {p.failover_backup_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
